import React, { useState } from "react";
import { List, ListItem, ListItemText, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import ConfirmationDialog from "./ConfirmationDialogComponent";
import { styled } from "@mui/system";
import colors from "assets/theme-dark/base/colors";

interface UniqueFilenamesProps {
  filenames: string[];
  onDelete: (sourceFileID: string) => void;
}

const StyledCard = styled(MDBox)`
  width: 100%;
  background: ${colors.background.default};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${colors.grey[300]};
  margin-top: 5rem;
  overflow: hidden;
  min-height: 15rem;
`;

const UniqueFilenames: React.FC<UniqueFilenamesProps> = ({ filenames, onDelete }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFilename, setSelectedFilename] = useState<string | null>(null);

  const handleOpenDialog = (filename: string) => {
    setSelectedFilename(filename);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setSelectedFilename(null);
    setDialogOpen(false);
  };

  const handleConfirmDelete = () => {
    if (selectedFilename) {
      onDelete(selectedFilename);
    }
    handleCloseDialog();
  };

  const sanitizeFilename = (filename: string): string => {
    return (
      filename
        .slice(0, -(6 + 1))
        .replace(/csv/gi, "")
        .trim() + ".csv"
    );
  };

  return (
    <StyledCard>
      <MDTypography sx={{ color: colors.text.main, mb: 1, fontSize: "1rem", textAlign: "center" }}>
        Imported Data from Files
      </MDTypography>
      <hr style={{ backgroundColor: colors.grey[900], marginBottom: "0.5rem" }} />
      {filenames.length === 0 ? (
        <MDTypography sx={{ color: colors.grey[500], textAlign: "center", mt: "2rem" }}>
          No files have been imported into this dataset.
        </MDTypography>
      ) : (
        <List
          sx={{
            padding: 0,
            maxHeight: "calc(15rem - 3rem)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {filenames.map((filename) => {
            const sanitizedName = sanitizeFilename(filename);
            const isLongFilename = sanitizedName.length >= 35;
            const displayName = isLongFilename ? `${sanitizedName.slice(0, 30)}...` : sanitizedName;

            return (
              <React.Fragment key={filename}>
                <ListItem
                  sx={{
                    padding: "0.5rem 0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {isLongFilename ? (
                    <Tooltip title={sanitizedName} arrow>
                      <ListItemText
                        primary={displayName}
                        primaryTypographyProps={{
                          sx: { color: colors.text.main, fontSize: "0.8rem" },
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <ListItemText
                      primary={displayName}
                      primaryTypographyProps={{
                        sx: { color: colors.text.main, fontSize: "0.8rem" },
                      }}
                    />
                  )}
                  <Tooltip title={`Delete all data from the table imported from: ${sanitizedName}`}>
                    <IconButton edge="end" aria-label="delete" size="small" onClick={() => handleOpenDialog(filename)}>
                      <DeleteIcon sx={{ color: "error.main" }} />
                    </IconButton>
                  </Tooltip>
                </ListItem>
                <ConfirmationDialog
                  open={dialogOpen && selectedFilename === filename}
                  onCancel={handleCloseDialog}
                  onConfirm={handleConfirmDelete}
                  title={sanitizedName}
                  message={"Are you sure you want to delete the data from the database?"}
                />
              </React.Fragment>
            );
          })}
        </List>
      )}
    </StyledCard>
  );
};

export default UniqueFilenames;
